import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useFormTemplates } from "../../../lib/queries/formTemplates";
import { useProjectIntakeLinks } from "../../../lib/queries/intakeLinks";
import { useCompanyMandates } from "../../../lib/queries/mandates";
import {
  useCreateProjectRequirement,
  useProjectRequirements,
} from "../../../lib/queries/projectCompleteness";
import { useProject } from "../../../lib/queries/projects";
import { AskClientDialog } from "../../projects/_parts/AskClientDialog";

import { PreparedMandateChoice } from "./PreparedMandateChoice";

export function AuthorizationStep({
  projectId,
  requestId,
  onDownload,
}: {
  projectId: string;
  requestId: string;
  onDownload: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const companyId = project.data?.company_id ?? undefined;
  const mandates = useCompanyMandates(companyId, projectId);
  const invitations = useProjectIntakeLinks(projectId);
  const templates = useFormTemplates({ status: "active" });
  const requirements = useProjectRequirements(projectId);
  const createRequirement = useCreateProjectRequirement(projectId);
  const qc = useQueryClient();
  const title = useRef<HTMLHeadingElement>(null);
  const [versionId, setVersionId] = useState("");
  const [dialog, setDialog] = useState<{
    id: string;
    kind: string;
    requirement_key: string;
    label: string;
    linkId?: string;
    contactId?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invited, setInvited] = useState(false);
  useEffect(() => {
    setVersionId("");
    setDialog(null);
    setError(null);
    setInvited(false);
  }, [projectId]);
  const active = mandates.data?.items.find(
    (m) => m.origin_project_id === projectId && m.is_valid && !m.revoked_at,
  );
  const available = (templates.data?.items ?? []).filter(
    (m) =>
      m.latest_published_version_id &&
      (m.latest_published_purpose === "mandate" || m.supports_client_signature),
  );
  const existing = requirements.data?.items.find(
    (r) => r.kind === "mandate" && r.project_id === projectId,
  );
  const chosen = existing?.requirement_key ?? (versionId || undefined);
  const pendingInvitations =
    invitations.data?.filter(
      (link) => link.status === "active" && link.form?.supports_client_signature,
    ) ?? [];
  useEffect(() => {
    if (mandates.dataUpdatedAt)
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", requestId] });
  }, [mandates.dataUpdatedAt, qc, requestId]);
  async function ask() {
    const selected =
      chosen ?? (available.length === 1 ? available[0]?.latest_published_version_id : undefined);
    if (
      !selected ||
      createRequirement.isPending ||
      invitations.isPending ||
      invitations.isError ||
      pendingInvitations.length > 0
    )
      return;
    setError(null);
    try {
      const requirement =
        existing ??
        (await createRequirement.mutateAsync({
          project_id: projectId,
          kind: "mandate",
          requirement_key: selected,
          label_fr: "Autorisation du client",
          label_en: "Client authorization",
          audience: "client",
          blocks_insurer_send: true,
        }));
      setDialog({
        id: requirement.id,
        kind: "mandate",
        requirement_key: selected,
        label: t("request_flow.authorization"),
      });
    } catch (err) {
      setError(
        err instanceof ApiException
          ? getApiErrorMessage(err, normalizeLang(i18n.language))
          : t("errors.generic"),
      );
    }
  }
  if (project.isError || mandates.isError || (!project.isPending && !companyId))
    return <p role="alert">{t("request_flow.authorization_unavailable")}</p>;
  if (project.isPending || mandates.isPending) return <p role="status">{t("app.loading")}</p>;
  return (
    <section className="space-y-4" aria-labelledby="authorization-title">
      <h2 ref={title} tabIndex={-1} id="authorization-title" className="text-2xl font-semibold">
        {t("request_flow.authorization")}
      </h2>
      <p className="text-sm text-slate-600">{t("flow.signature_roles")}</p>
      {active ? (
        <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <p className="font-medium">{t("request_flow.mandate_reused")}</p>
          <p>
            {active.signed_by_name} — {new Date(active.signed_at).toLocaleDateString(i18n.language)}
            {active.version_number
              ? ` · ${t("request_flow.version", { number: active.version_number })}`
              : ""}
          </p>
          <p className="text-sm">{t("request_flow.mandate_company_scope")}</p>
          {active.artifact_available && (active.artifact_file_id || active.source_file_id) ? (
            <button
              className="btn min-h-11"
              type="button"
              onClick={() => onDownload((active.artifact_file_id ?? active.source_file_id)!)}
            >
              {t("request_flow.open_signed_pdf")}
            </button>
          ) : (
            <p role="alert" className="text-red-700">
              {t("request_flow.pdf_missing")}
            </p>
          )}
        </div>
      ) : (
        <>
          <p className="text-slate-600">{t("request_flow.mandate_help")}</p>
          {invitations.isPending ? (
            <p role="status">{t("app.loading")}</p>
          ) : invitations.isError ? (
            <p role="alert">{t("errors.generic")}</p>
          ) : pendingInvitations.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" role="status">
              <p className="font-medium">{t("flow_clarity.waiting_signature")}</p>
              <p className="mt-1 text-sm">{t("flow_clarity.client_next")}</p>
              <ul className="mt-3 space-y-2">
                {pendingInvitations.map((link) => (
                  <li key={link.id}>
                    <p>
                      {link.form?.name} ·{" "}
                      {t("request_flow.version", { number: link.form?.version_number })}
                    </p>
                    <p>{link.contact?.name ?? t("flow_clarity.contact_unknown")}</p>
                    <p>
                      {t(
                        `flow_clarity.invitation_${link.invitation_status === undefined ? "unknown" : (link.invitation_status ?? "prepared")}`,
                      )}
                    </p>
                    {link.invitation_status !== undefined &&
                      link.invitation_status !== "queued" &&
                      link.invitation_status !== "sent" && (
                        <button
                          type="button"
                          className="btn min-h-11"
                          onClick={() =>
                            setDialog({
                              id: existing?.id ?? link.id,
                              kind: "mandate",
                              requirement_key: link.form_template_version_id ?? "",
                              label: t("request_flow.authorization"),
                              linkId: link.id,
                              contactId: link.contact?.id,
                            })
                          }
                        >
                          {t("flow_clarity.send_existing_invitation")}
                        </button>
                      )}
                    <Link
                      className="text-brand-700 inline-flex min-h-11 items-center underline"
                      to={`/intake-links/${link.id}`}
                    >
                      {t("flow_clarity.view_invitation")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : templates.isPending || requirements.isPending ? (
            <p role="status">{t("app.loading")}</p>
          ) : templates.isError || requirements.isError ? (
            <p role="alert">{t("errors.generic")}</p>
          ) : (
            <>
              {existing && (
                <PreparedMandateChoice
                  key={`${projectId}:${existing.id}`}
                  projectId={projectId}
                  requirementId={existing.id}
                  used={Boolean(
                    mandates.data?.items.some((m) => m.origin_project_id === projectId) ||
                    invitations.data?.some(
                      (link) =>
                        link.form_template_version_id?.toLowerCase() ===
                        existing.requirement_key.toLowerCase(),
                    ),
                  )}
                  onRetired={() => {
                    setVersionId("");
                    setDialog(null);
                    setInvited(false);
                    title.current?.focus();
                  }}
                />
              )}
              {!existing && available.length === 0 && (
                <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                  {t("request_flow.no_mandate_template")}
                </p>
              )}
              {!existing && available.length > 1 && (
                <label className="block">
                  {t("request_flow.authorization_model")}
                  <select
                    className="input"
                    value={versionId}
                    onChange={(e) => setVersionId(e.target.value)}
                  >
                    <option value="">{t("request_flow.choose")}</option>
                    {available.map((m) => (
                      <option key={m.id} value={m.latest_published_version_id!}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                disabled={createRequirement.isPending || (!chosen && available.length !== 1)}
                onClick={() => void ask()}
                className="btn min-h-11 border border-slate-300"
              >
                {t("request_flow.ask_signature")}
              </button>
            </>
          )}
          {invited && <p role="status">{t("request_flow.waiting_signature")}</p>}
        </>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {dialog && (
        <AskClientDialog
          projectId={projectId}
          companyId={companyId ?? null}
          initialContactId={dialog.contactId ?? project.data?.contact_id}
          existingIntakeLinkId={dialog.linkId}
          requirement={dialog}
          missingInformationId={null}
          onClose={() => setDialog(null)}
          onSent={() => setInvited(true)}
        />
      )}
    </section>
  );
}
